
import {
  transformTenant,
  transformTenantCreate,
  transformTenantRole,
  transformTenantRow,
  transformTenantStorage,
  transformTenantType,
  transformTenantUpdate,
  transformTenantUserCreate,
  transformTenantUserRow,
} from "./transformation";
import {
  Tenant,
  TenantBankAccount,
  TenantBankAccountEditFormValues,
  TenantCreateFormValues,
  TenantEditFormValues,
  TenantRole,
  TenantRow,
  TenantStorage,
  TenantType,
  TenantUserFormValues,
  TenantUserRow,
} from "./types";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "../transformation";
import { FleetDocumentUpload } from "../fleet/vehicles/documents/types";
import { DataWithPagination } from "services/api/types";
import { Pagination } from "services/search";

/*---------------------------------------------------------------------------------------
  TENANT - SEARCH
-----------------------------------------------------------------------------------------*/
export const searchTenantsAPI = async (
  pagination: Pagination,
  name?: string | null
): Promise<DataWithPagination<TenantRow>> => {
  let path = `/v1/users/tenants?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformTenantRow);
};

/*---------------------------------------------------------------------------------------
  TENANT - CREATE
-----------------------------------------------------------------------------------------*/
export const createTenantAPI = async (
  formValues: TenantCreateFormValues
): Promise<Tenant> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants`,
      method: "POST",
      body: transformTenantCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste kreirali tenanta",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenant(response.data);
};

/*---------------------------------------------------------------------------------------
  TENANT - USERS
-----------------------------------------------------------------------------------------*/
export const searchTenantUsersAPI = async (
  tenantId: string,
  pagination: Pagination,
  name?: string | null,
  email?: string | null,
  phone?: string | null,
  address?: string | null,
  roleId?: string | null,
  officeId?: string | null
): Promise<DataWithPagination<TenantUserRow>> => {
  let path = `/v1/users/tenants/${tenantId}/users?query=`;

  if (name) path += `${name}`;
  if (email) path += `&email=${email}`;
  if (phone) path += `&phone=${phone}`;
  if (address) path += `&address=${address}`;
  if (roleId) path += `&roleId=${roleId}`;
  if (officeId) path += `&officeId=${officeId}`;
  return await paginatedApiCall(path, pagination, transformTenantUserRow);
};

export const createTenantUserAPI = async (
  tenantId: string,
  formValues: TenantUserFormValues
): Promise<TenantUserRow> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/users`,
      method: "POST",
      body: transformTenantUserCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste kreirali korisnika",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenantUserRow(response.data);
};

export const updateTenantUserAPI = async (
  tenantId: string,
  userId: string,
  formValues: TenantUserFormValues
): Promise<TenantUserRow> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/users/${userId}`,
      method: "PUT",
      body: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        roles: formValues.roleId ? [formValues.roleId] : [],
      },
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste ažurirali korisnika",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenantUserRow(response.data);
};

export const deleteTenantUserAPI = async (
  tenantId: string,
  userId: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/users/${userId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Korisnik uspješno obrisan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
  TENANT - ROLES
-----------------------------------------------------------------------------------------*/
export const getTenantRolesAPI = async (
  tenantId: string
): Promise<TenantRole[]> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/roles`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data ?? []).map(transformTenantRole);
};


/*---------------------------------------------------------------------------------------
  TENANT TYPE
-----------------------------------------------------------------------------------------*/

export const getTenantTypesAPI = async (): Promise<TenantType[]> => {
  var response = [
    {
      id: "C",
      name: "Tvrtka",
    },
    {
      id: "P",
      name: "Osoba",
    },
  ];

  return response.map(transformTenantType);
};

/*---------------------------------------------------------------------------------------
  TENANT - BY ID
-----------------------------------------------------------------------------------------*/
export const getTenantAPI = async (tenantId: string): Promise<Tenant> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenant(response.data);
};

/*---------------------------------------------------------------------------------------
  TENANT - STORAGE (zauzeće diska)
-----------------------------------------------------------------------------------------*/
export const getTenantStorageAPI = async (tenantId: string): Promise<TenantStorage> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/storage`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenantStorage(response.data);
};

export const getAllTenantsStorageAPI = async (): Promise<TenantStorage[]> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/storage`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data ?? []).map(transformTenantStorage);
};


/*---------------------------------------------------------------------------------------
  TENANT - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateTenantAPI = async (
  tenantId: string,
  tenant: TenantEditFormValues,
  logoUploads: FleetDocumentUpload[]
): Promise<Tenant> => {

  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}`,
      method: "PUT",
      body: transformTenantUpdate(tenant, logoUploads),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste ažurirali zakupnika",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenant(response.data);
};

/*---------------------------------------------------------------------------------------
  TENANT - LOGO
-----------------------------------------------------------------------------------------*/
export const updateTenantLogoAPI = async (
  tenantId: string,
  logoUploadId: string | null
): Promise<Tenant> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/logo`,
      method: "PATCH",
      body: { logoUploadId },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformTenant(response.data);
};

/*---------------------------------------------------------------------------------------
  TENANT BANK ACCOUNTS
-----------------------------------------------------------------------------------------*/
export const getTenantBankAccountsAPI = async (
  tenantId: string
): Promise<TenantBankAccount[]> => {
  const response = await createApiCall({
    path: `/v1/users/tenants/${tenantId}/bank-accounts`,
    method: "GET",
  })();
  return response.data ?? [];
};

export const createTenantBankAccountAPI = async (
  tenantId: string,
  formValues: TenantBankAccountEditFormValues
): Promise<TenantBankAccount> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/bank-accounts`,
      method: "POST",
      body: formValues,
    },
    {
      getMessage: {
        success: () => "Bankovni račun uspješno dodan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data;
};

export const updateTenantBankAccountAPI = async (
  tenantId: string,
  id: string,
  formValues: TenantBankAccountEditFormValues
): Promise<TenantBankAccount> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/bank-accounts/${id}`,
      method: "PUT",
      body: formValues,
    },
    {
      getMessage: {
        success: () => "Bankovni račun uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data;
};

export const deleteTenantBankAccountAPI = async (
  tenantId: string,
  id: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/users/tenants/${tenantId}/bank-accounts/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Bankovni račun uspješno obrisan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data?.deleted;
};
